import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../lib/config';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const API = `${API_BASE}`;

const CategoryManagement = ({ onBack }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    description: '',
    display_order: 0,
    is_active: true
  });

  // カテゴリ一覧取得
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      } else {
        toast.error('カテゴリの取得に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // デフォルトカテゴリ初期化
  const initializeDefaultCategories = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/categories/initialize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'カテゴリ初期化に失敗しました');
      }
    } catch (error) {
      console.error('Error initializing categories:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // カテゴリ作成
  const createCategory = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('カテゴリを作成しました');
        setShowCreateForm(false);
        resetForm();
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'カテゴリ作成に失敗しました');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // カテゴリ更新
  const updateCategory = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('カテゴリを更新しました');
        setEditingCategory(null);
        resetForm();
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'カテゴリ更新に失敗しました');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // カテゴリ削除
  const deleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`「${categoryName}」を削除しますか？このカテゴリを使用している事業者からも削除されます。`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('カテゴリを削除しました');
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'カテゴリ削除に失敗しました');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // フォームリセット
  const resetForm = () => {
    setFormData({
      name: '',
      icon: '',
      description: '',
      display_order: 0,
      is_active: true
    });
  };

  // 編集開始
  const startEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      icon: category.icon,
      description: category.description || '',
      display_order: category.display_order,
      is_active: category.is_active
    });
  };

  // 表示順変更
  const changeDisplayOrder = async (categoryId, newOrder) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/categories/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ display_order: newOrder })
      });

      if (response.ok) {
        fetchCategories();
      } else {
        toast.error('表示順の変更に失敗しました');
      }
    } catch (error) {
      console.error('Error changing display order:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  // フォーム送信
  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCategory) {
      updateCategory();
    } else {
      createCategory();
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <div className="p-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">カテゴリマスタ管理</h2>
          <p className="text-gray-600 mt-1">事業者のカテゴリ分類を管理します</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={initializeDefaultCategories}
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-yellow-600 transition-colors"
          >
            デフォルトカテゴリ作成
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            カテゴリ追加
          </button>
        </div>
      </div>

      {/* カテゴリ作成・編集フォーム */}
      {(showCreateForm || editingCategory) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium mb-4">
            {editingCategory ? 'カテゴリ編集' : 'カテゴリ作成'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  カテゴリ名 *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: 飲食"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  アイコン *
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({...formData, icon: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="例: "
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                説明
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="カテゴリの説明を入力してください"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  表示順
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ステータス
                </label>
                <select
                  value={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.value === 'true'})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="true">有効</option>
                  <option value="false">無効</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors"
              >
                {editingCategory ? '更新' : '作成'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingCategory(null);
                  resetForm();
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* カテゴリ一覧 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">カテゴリ一覧</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500">読み込み中...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 mb-4">カテゴリが登録されていません</p>
            <button
              onClick={initializeDefaultCategories}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors"
            >
              デフォルトカテゴリを作成
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    表示順
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    アイコン
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    カテゴリ名
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    説明
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    事業者数
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ステータス
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-1">
                        <span>{category.display_order}</span>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => changeDisplayOrder(category.id, category.display_order - 1)}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={category.display_order <= 1}
                          >
                            <ArrowUpIcon className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => changeDisplayOrder(category.id, category.display_order + 1)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <ArrowDownIcon className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="text-2xl">{category.icon}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {category.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {category.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        {category.employer_count}件
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          category.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {category.is_active ? '有効' : '無効'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(category)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteCategory(category.id, category.name)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManagement;